import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "./AuthContext";
import { Bell, Zap, CheckCheck } from "lucide-react";
import { logError } from "../lib/log";

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return "just now";
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function useNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        axios.get(`${API_BASE}/notifications`),
        axios.get(`${API_BASE}/notifications/unread-count`),
      ]);
      setItems(list.data?.items ?? list.data);  // envelope { items,total,has_more } or legacy bare list
      setUnread(count.data.count);
    } catch (err) {
      // 401/403 expected when anonymous — only log dev-side
      if (err?.response?.status !== 401 && err?.response?.status !== 403) {
        logError("notifications-bell:refresh", err);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 20000);
    return () => clearInterval(id);
  }, [refresh]);

  const markAll = useCallback(async () => {
    await axios.post(`${API_BASE}/notifications/read-all`);
    refresh();
  }, [refresh]);
  const markOne = useCallback(async (id) => {
    await axios.post(`${API_BASE}/notifications/${id}/read`);
    refresh();
  }, [refresh]);

  return { items, unread, markAll, markOne, refresh };
}

function useOutsideClick(ref, onOutside) {
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, onOutside]);
}

function kindColor(kind) {
  if (kind === "success") return "var(--moss)";
  if (kind === "warning") return "var(--accent)";
  return "var(--ink-2)";
}

function NotificationItem({ n, onClick }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => { onClick(n.notification_id); if (n.ref?.deep_link) navigate(n.ref.deep_link); }}
      className={`w-full text-left px-4 py-3 border-b border-[var(--border)] last:border-0 transition flex gap-3 ${n.read ? "" : "bg-[var(--subtle)]"}`}
      data-testid={`notif-item-${n.notification_id}`}>
      <span className="mt-0.5" style={{ color: kindColor(n.kind) }}>
        <Zap size={14} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] text-[var(--ink)] font-medium leading-tight">{n.title}</span>
        {n.body && <span className="block text-[12px] text-[var(--ink-2)] mt-0.5 leading-snug">{n.body}</span>}
        <span className="block text-[10px] text-[var(--ink-3)] font-mono mt-1">{timeAgo(n.created_at)}</span>
      </span>
      {!n.read && <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: "var(--accent)" }} />}
    </button>
  );
}

function NotificationsPanel({ items, unread, onMarkAll, onItem }) {
  return (
    <div className="absolute left-full top-0 ml-2 w-96 card overflow-hidden shadow-xl z-50"
         style={{ boxShadow: "0 16px 40px -16px rgba(26,24,20,0.3)" }}
         data-testid="notif-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
        <div className="overline">Activity</div>
        {unread > 0 && (
          <button onClick={onMarkAll} className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1" data-testid="notif-mark-all">
            <CheckCheck size={12} /> Mark all read
          </button>
        )}
      </div>
      <div className="max-h-[min(24rem,55vh)] overflow-y-auto">
        {items.length === 0 ? (
          <div className="px-4 py-10 text-center text-[12px] text-[var(--ink-3)]">
            Nothing yet. Move a job in the Kanban to trigger your first automator.
          </div>
        ) : items.map((n) => (
          <NotificationItem key={n.notification_id} n={n} onClick={onItem} />
        ))}
      </div>
    </div>
  );
}

export default function NotificationsBell() {
  const { items, unread, markAll, markOne, refresh } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClick(ref, useCallback(() => setOpen(false), []));

  const toggle = () => {
    setOpen((v) => !v);
    if (!open) refresh();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-[13px] text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)] transition"
        data-testid="notif-bell"
      >
        <span className="flex items-center gap-2"><Bell size={14} /> Notifications</span>
        {unread > 0 && (
          <span className="badge badge-accent" data-testid="notif-unread-count">{unread}</span>
        )}
      </button>
      {open && <NotificationsPanel items={items} unread={unread} onMarkAll={markAll} onItem={markOne} />}
    </div>
  );
}
